import { OPERATIONS_ROUTES } from '@/constant/route'
import { useAuthContext } from '@/features/auth/context'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { useAppDispatch } from '@/store/stores'
import { APIResponseList } from '@/types/common'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

interface UseValidateCurrentInterfaceProps {
  currentInterfaceId?: string
  allInterfaces?: APIResponseList<any>
  redirectTo?: string
  autoRedirect?: boolean
}

interface UseValidateCurrentInterfaceReturn {
  isValidInterface: boolean
  selectedInterface: any
  isLoading: boolean
  isPendingSetup: boolean
  validateInterface: (id?: string) => boolean
}

export const useValidateCurrentInterface = ({
  currentInterfaceId,
  allInterfaces,
  redirectTo = OPERATIONS_ROUTES.index.to,
  autoRedirect = true,
}: UseValidateCurrentInterfaceProps): UseValidateCurrentInterfaceReturn => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { interfaceId: contextInterfaceId } = useAuthContext()

  // Use route param interfaceId if available, otherwise fall back to context
  const effectiveInterfaceId = currentInterfaceId ?? (contextInterfaceId ? String(contextInterfaceId) : undefined)

  const selectedInterface = useMemo(() => {
    if (!effectiveInterfaceId || !allInterfaces?.list) return undefined

    return allInterfaces.list.find(
      (i: any) => Number(i?.id) === Number(effectiveInterfaceId),
    )
  }, [effectiveInterfaceId, allInterfaces?.list])

  const isValidInterface = useMemo(() => {
    if (!effectiveInterfaceId || !allInterfaces?.list) return false
    return Boolean(selectedInterface)
  }, [effectiveInterfaceId, allInterfaces?.list, selectedInterface])

  // Interface is pending setup when status === false
  const isPendingSetup = useMemo(() => {
    if (!selectedInterface) return false
    return selectedInterface.status === false
  }, [selectedInterface])

  const isLoading = useMemo(() => {
    return Boolean(effectiveInterfaceId && !allInterfaces?.list)
  }, [effectiveInterfaceId, allInterfaces?.list])

  const validateInterface = useMemo(() => {
    return (id?: string) => {
      if (!id || !allInterfaces?.list) return false
      return Boolean(
        allInterfaces.list.find((i: any) => Number(i?.id) === Number(id)),
      )
    }
  }, [allInterfaces?.list])

  useEffect(() => {
    if (autoRedirect && effectiveInterfaceId && allInterfaces?.list) {
      // Only redirect when interface is found AND already active (status === true)
      // Do NOT redirect when interface is not found (!selectedInterface) to prevent
      // redirect loops caused by data source timing differences between react-query
      // and Redux store during refetches
      if (selectedInterface?.status === true) {
        // Sync Redux store before leaving setup to prevent an infinite redirect loop:
        // SelectLabelDashboard on the dashboard reads status from Redux (stale false)
        // while this hook reads react-query (fresh true) - a mismatch bounces the user
        // back into setup forever. Same pattern as SetUpSecondV2 (20260331-setup-loop).
        dispatch(
          updateInterfaceStatus({
            id: Number(effectiveInterfaceId),
            status: true,
          }),
        )
        navigate(redirectTo)
      }
    }
  }, [
    allInterfaces,
    effectiveInterfaceId,
    selectedInterface,
    navigate,
    redirectTo,
    autoRedirect,
    dispatch,
  ])

  return {
    isValidInterface,
    selectedInterface,
    isLoading,
    isPendingSetup,
    validateInterface,
  }
}

export default useValidateCurrentInterface
